import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, FolderPlus } from "lucide-react";
import { Button, Field, Select } from "@/design/components";
import { CreateProjectModal } from "@/components/CreateProjectModal";
import { useActiveProgrammeId } from "@/domain/hooks";

/**
 * "Create project" for the cross-deal Projects screens (StratusProjectsPage /
 * FunderProjectsPage). Unlike WorkspacePages.tsx's WorkspaceProjectsPage —
 * already scoped to one deal, so it hands CreateProjectModal a fixed
 * programmeId — this screen spans every deal, so the target deal isn't known
 * until the user picks one. A single deal skips the picker entirely; more
 * than one shows a small "which deal" dialog first, then opens the same,
 * unmodified CreateProjectModal against that deal's real programmeId
 * (resolved the same way WorkspacePages does — useActiveProgrammeId(workspaceId),
 * not the seed-derived id ProgrammeSummary carries).
 */
export function CreateProjectAction({ deals }: { deals: { workspaceId: string; name: string }[] }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedWorkspaceId, setPickedWorkspaceId] = useState("");
  const [createWorkspaceId, setCreateWorkspaceId] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const programmeId = useActiveProgrammeId(createWorkspaceId);

  if (deals.length === 0) return null;

  function openFor(workspaceId: string) {
    setCreateWorkspaceId(workspaceId);
    setPickerOpen(false);
    setCreateOpen(true);
  }

  return (
    <>
      <Button
        onClick={() => {
          if (deals.length === 1) {
            openFor(deals[0].workspaceId);
          } else {
            setPickedWorkspaceId(deals[0].workspaceId);
            setPickerOpen(true);
          }
        }}
      >
        <FolderPlus className="h-4 w-4" /> Create project
      </Button>

      <Dialog.Root open={pickerOpen} onOpenChange={setPickerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-card border border-line bg-card p-6 shadow-overlay animate-fade-in">
            <div className="mb-3 flex items-start justify-between gap-3">
              <Dialog.Title className="text-base font-semibold tracking-tight text-ink">Which deal?</Dialog.Title>
              <Dialog.Close asChild>
                <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <div className="space-y-4">
              <Field label="Deal">
                <Select value={pickedWorkspaceId} onChange={(e) => setPickedWorkspaceId(e.target.value)}>
                  {deals.map((d) => (
                    <option key={d.workspaceId} value={d.workspaceId}>{d.name}</option>
                  ))}
                </Select>
              </Field>
              <div className="flex items-center justify-end gap-2">
                <Dialog.Close asChild>
                  <Button variant="ghost">Cancel</Button>
                </Dialog.Close>
                <Button onClick={() => openFor(pickedWorkspaceId)}>Continue</Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <CreateProjectModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        programmeId={programmeId}
        workspaceId={createWorkspaceId}
      />
    </>
  );
}
